import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { shuffle } from "../lib/content";
import { sound } from "../lib/sounds";
import { speak } from "../lib/speech";

// Classic memory / pairs game. Calls onComplete(true) when all pairs found.
export const MemoryGame = ({ round, onComplete }) => {
  const cards = useMemo(() => {
    const deck = round.pairs.flatMap((emoji, idx) => ([
      { id: `${idx}-a`, emoji, pair: idx },
      { id: `${idx}-b`, emoji, pair: idx },
    ]));
    return shuffle(deck);
  }, [round]);

  const [flipped, setFlipped] = useState([]); // indices currently face-up (unmatched)
  const [matched, setMatched] = useState([]); // pair ids matched
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    speak("Vind de dezelfde dieren. Onthoud goed!");
  }, []);

  const flip = (i) => {
    if (busy || flipped.includes(i) || matched.includes(cards[i].pair)) return;
    sound.pop();
    const next = [...flipped, i];
    setFlipped(next);
    if (next.length === 2) {
      setBusy(true);
      const [a, b] = next;
      if (cards[a].pair === cards[b].pair) {
        setTimeout(() => {
          sound.correct();
          const nm = [...matched, cards[a].pair];
          setMatched(nm);
          setFlipped([]);
          setBusy(false);
          if (nm.length === round.pairs.length) {
            speak("Alle paren gevonden! Super!");
            setTimeout(() => onComplete(true), 700);
          }
        }, 500);
      } else {
        setTimeout(() => { setFlipped([]); setBusy(false); }, 800);
      }
    }
  };

  const cols = cards.length <= 8 ? 4 : cards.length <= 12 ? 4 : 4;

  return (
    <div className="flex flex-col items-center gap-4 w-full">
      <p className="font-display text-xl font-bold text-slate-700">Vind de paren!</p>
      <div className="grid gap-3 w-full max-w-md" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
        {cards.map((c, i) => {
          const isUp = flipped.includes(i) || matched.includes(c.pair);
          return (
            <motion.button
              key={c.id}
              data-testid={`memory-card-${i}`}
              onClick={() => flip(i)}
              whileTap={{ scale: 0.95 }}
              className="relative aspect-square rounded-2xl shadow-md"
              style={{ perspective: 600 }}
            >
              <motion.div
                animate={{ rotateY: isUp ? 180 : 0 }}
                transition={{ duration: 0.4 }}
                style={{ transformStyle: "preserve-3d" }}
                className="relative h-full w-full"
              >
                <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-gradient-to-br from-sky-400 to-sky-500 text-white text-2xl font-display font-bold"
                     style={{ backfaceVisibility: "hidden" }}>
                  ?
                </div>
                <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-white text-4xl"
                     style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
                  {c.emoji}
                </div>
              </motion.div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default MemoryGame;
